import { isDefined } from '@sgme/fp';
import { memo, useEffect } from 'react';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardCommercialMargin } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardCommercialMargin/index.tsx';
import { FlexiForwardGreeks } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardGreeks/index.tsx';
import { FlexiForwardHeader } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHeader.tsx';
import { FlexiForwardHedgesForm } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm';
import { FlexiForwardMainFields } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/index.tsx';
import { FlexiForwardPriceSummary } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/index.tsx';
import { FlexiForwardTileFooter } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardTileFooter';
import { IsInternalSales } from '@/App/tiles/Option/utils/IsInternalSales';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type FlexiForwardTileProps = {
  tileId: string;
};

export const FlexiForwardTile = memo(({ tileId }: FlexiForwardTileProps) => {
  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));
  const dispatch = useAppDispatch();
  const { currencyPair, executionStatus } = useFlexiForwardSelector((flexiForward) => {
    const { execution, properties } = flexiForward;

    return {
      currencyPair: properties.currencyPair,
      executionStatus: execution?.status,
    };
  });

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (isDefined(executionStatus)) {
    return <ExecutionStatus status={executionStatus} />;
  }

  return (
    <div className="bg-transparent border-primary w-100 h-100">
      <AutoAdvanceForm className="d-flex flex-column bg-lvl1 py-2 w-100 h-100">
        <FlexiForwardHeader tileId={tileId} />

        {isDefined(currencyPair) && (
          <>
            <FlexiForwardMainFields tileId={tileId} />

            <FlexiForwardPriceSummary tileId={tileId} />

            <FlexiForwardHedgesForm tileId={tileId} />

            <IsInternalSales>
              <FlexiForwardCommercialMargin tileId={tileId} />
            </IsInternalSales>

            <FlexiForwardGreeks />

            <FlexiForwardTileFooter tileId={tileId} />
          </>
        )}
      </AutoAdvanceForm>
    </div>
  );
});
